import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Clock, ShieldCheck, ShieldAlert } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * <WalletHoldBanner /> — Shows the 48-hour new-buyer security-hold state
 * on the buyer's wallet. Renders nothing when the hold is not active.
 * Polls once/minute so the countdown stays live without hitting the
 * server on every user interaction.
 */
const WalletHoldBanner = ({ token }) => {
  const [state, setState] = useState(null);

  const load = useCallback(async () => {
    if (!token) return;
    try {
      const { data } = await axios.get(`${API}/wallet/hold-status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setState(data);
    } catch (_e) { /* silent */ }
  }, [token]);

  useEffect(() => {
    load();
    const iv = setInterval(load, 60000);
    return () => clearInterval(iv);
  }, [load]);

  if (!state) return null;
  if (!state.active) {
    // Show a subtle "cleared" indicator when the buyer has a balance but
    // no hold (i.e. they've been whitelisted).
    if (state.balance_cents > 0) {
      return (
        <section
          className="rounded-xl border border-[#8DB87A]/30 bg-[#8DB87A]/[0.06] p-3 flex items-center gap-3"
          data-testid="wallet-hold-cleared"
        >
          <ShieldCheck className="w-4 h-4 text-[#8DB87A]" />
          <p className="text-xs text-[#8DB87A]">
            Wallet whitelisted — top-ups clear instantly.
          </p>
        </section>
      );
    }
    return null;
  }

  const hours = state.hours_remaining || 0;
  const displayTime = hours > 24
    ? `${(hours / 24).toFixed(1)} days`
    : `${hours.toFixed(1)}h`;

  return (
    <section
      className="rounded-xl border border-[#EBB84C]/40 bg-gradient-to-br from-[#EBB84C]/[0.10] to-[#0A0E18] p-4"
      data-testid="wallet-hold-banner"
    >
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-[#EBB84C]/15 border border-[#EBB84C]/40 flex items-center justify-center flex-shrink-0">
          <ShieldAlert className="w-5 h-5 text-[#EBB84C]" />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <p className="text-[10px] uppercase tracking-widest text-[#EBB84C] font-bold">Security hold</p>
            <span className="inline-flex items-center gap-1 text-[10px] text-[#8A92A0]">
              <Clock className="w-3 h-3" /> {displayTime} left
            </span>
          </div>
          <h3 className="font-['Unbounded'] text-base font-bold text-white">First-top-up 48-hour hold</h3>
          <p className="text-xs text-[#8A92A0] mt-1">
            For your safety we&apos;re holding your first{" "}
            <strong className="text-white">${(state.held_cents / 100).toFixed(2)}</strong> of Tokens until{" "}
            <span className="text-white font-mono text-[11px]">
              {state.hold_until ? new Date(state.hold_until).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : ""}
            </span>. Every future top-up clears instantly.
          </p>
          <div className="grid grid-cols-2 gap-3 mt-3 text-xs">
            <div className="rounded bg-black/40 border border-[#1A2030] p-2">
              <p className="text-[10px] uppercase tracking-widest text-[#8A92A0]">Wallet balance</p>
              <p className="text-white font-bold">${(state.balance_cents / 100).toFixed(2)}</p>
            </div>
            <div className="rounded bg-black/40 border border-[#1A2030] p-2">
              <p className="text-[10px] uppercase tracking-widest text-[#8DB87A]">Spendable now</p>
              <p className="text-[#8DB87A] font-bold">${(state.spendable_cents / 100).toFixed(2)}</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WalletHoldBanner;
